/**
 * useOptinAdAttribution.ts
 *
 * Fetches real optin counts per utm_content from the backend,
 * then provides a lookup function that matches ads (by ad_id or ad_name)
 * to their real optin count from MongoDB.
 *
 * Matching logic:
 *   1. Exact match: utm_content === ad_id
 *   2. Name match: ad_name (lowercased) contains utm_content (lowercased)
 */

import { useState, useEffect, useCallback } from "react";

/** Map of utm_content → optin count */
export type OptinAdMap = Map<string, number>;

export interface OptinAdAttribution {
  /** Raw map of utm_content → count */
  rawMap: OptinAdMap;
  /** Look up real optin count for an ad by its ID and name */
  getOptinCount: (adId: string, adName: string) => number | null;
  loading: boolean;
  error: string | null;
}

export function useOptinAdAttribution(): OptinAdAttribution {
  const [rawMap, setRawMap] = useState<OptinAdMap>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const resp = await fetch("/api/optin-ad-attribution");
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const json = (await resp.json()) as {
          success: boolean;
          data: Record<string, number>;
        };
        if (!cancelled) {
          const map = new Map<string, number>();
          for (const [key, count] of Object.entries(json.data ?? {})) {
            map.set(key.trim(), count);
          }
          setRawMap(map);
        }
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const getOptinCount = useCallback(
    (adId: string, adName: string): number | null => {
      if (rawMap.size === 0) return null;

      // 1. Exact match on ad_id
      const byId = rawMap.get(adId.trim());
      if (byId !== undefined) return byId;

      // 2. Check if any utm_content key matches within the ad_name
      //    e.g. utm_content="vid3" matches ad_name="UU | vid3 - broad"
      const adNameLower = adName.toLowerCase().trim();
      for (const [utmContent, count] of rawMap) {
        const utmLower = utmContent.toLowerCase();
        // Skip very short keys (1-2 chars) to avoid false positives
        if (utmLower.length < 3) continue;
        if (adNameLower.includes(utmLower)) return count;
      }

      return null;
    },
    [rawMap]
  );

  return { rawMap, getOptinCount, loading, error };
}
